import React from "react";
import { SectionLabel } from "./ProfileDashboardUI";

const OverviewIcon = ({ type, color }) => {
  const common = {
    width: "17",
    height: "17",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  const icons = {
    rfq: (
      <svg {...common}>
        <path d="M7 3h7l3 3v15H7z" />
        <path d="M14 3v4h4" />
        <path d="M10 12h4M10 16h3" />
      </svg>
    ),
    progress: (
      <svg {...common}>
        <rect x="5" y="3" width="14" height="18" rx="2" />
        <path d="M9 8h6M9 12h6M9 16h3" />
      </svg>
    ),
    po: (
      <svg {...common}>
        <rect x="6" y="4" width="12" height="16" rx="2" />
        <path d="M9 8h6M9 12h6M9 16h4" />
      </svg>
    ),
    contract: (
      <svg {...common}>
        <path d="M7 3h7l3 3v15H7z" />
        <path d="M10 10h5M10 14h5M10 18h3" />
        <path d="M14 3v4h4" />
      </svg>
    ),
    value: (
      <svg {...common}>
        <circle cx="12" cy="12" r="8" />
        <path d="M9 9h6M9 12h5M12 9c2 0 2 6-1 6H9" />
      </svg>
    ),
    savings: (
      <svg {...common}>
        <circle cx="12" cy="12" r="8" />
        <path d="M12 8v8M9.5 10.5c.4-1 1.3-1.5 2.5-1.5 1.4 0 2.5.7 2.5 1.8 0 2.6-5 1.1-5 3.6 0 1.1 1 1.8 2.5 1.8 1.2 0 2.1-.5 2.5-1.5" />
      </svg>
    ),
  };

  return icons[type] || icons.rfq;
};

const OverviewCard = ({ icon, label, value, period, trend, accent, bubble, onClick, tokens }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className={`group relative overflow-hidden rounded-2xl border text-left shadow-[0_10px_26px_rgba(15,23,42,0.06)] transition-all duration-200 ${
      onClick ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_18px_38px_rgba(15,23,42,0.12)]" : "cursor-default"
    }`}
    style={{ borderColor: tokens.line, backgroundColor: tokens.card }}
  >
    <span
      className="absolute inset-x-0 top-0 h-1"
      style={{ background: `linear-gradient(90deg, ${accent}, ${bubble})` }}
    />

    <div className="min-h-[132px] px-4 pt-5 pb-4">
      <div className="flex items-start justify-between gap-3">
        <div
          className="flex h-11 w-11 items-center justify-center rounded-2xl"
          style={{ backgroundColor: bubble }}
        >
          <div
            className="flex h-7 w-7 items-center justify-center rounded-xl shadow-sm"
            style={{ backgroundColor: accent }}
          >
            <OverviewIcon type={icon} color="#FFFFFF" />
          </div>
        </div>

        <span
          className="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide"
          style={{ backgroundColor: tokens.cardSoft, color: tokens.faint }}
        >
          {period}
        </span>
      </div>

      <div className="mt-4">
        <p className="text-[13px] font-bold leading-tight" style={{ color: tokens.ink }}>
          {label}
        </p>
        <div className="mt-3 flex items-end justify-between gap-3">
          <span className="text-[23px] font-bold leading-none tracking-tight" style={{ color: tokens.ink }}>
            {value}
          </span>
          {onClick && (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold opacity-90 transition-opacity group-hover:opacity-100"
              style={{ backgroundColor: bubble, color: accent }}
            >
              View RFQs
              <span className="text-[13px] leading-none">→</span>
            </span>
          )}
        </div>
      </div>
    </div>

    <div className="border-t px-4 py-3" style={{ borderColor: tokens.line, backgroundColor: tokens.cardSoft }}>
      <p className="flex items-center gap-1.5 text-[11px] font-semibold text-[#16A05D]">
        <span className="flex h-4 w-4 items-center justify-center rounded bg-[#E7F8EF] text-[10px] leading-none text-[#16A05D]">
          ↗
        </span>
        <span>{trend}</span>
      </p>
    </div>
  </button>
);

const ProcurementActivity = ({ stats, tokens, onNavigate }) => {
  const overviewCards = stats?.overviewCards || [];
  const navigationTargets = stats?.navigationTargets || {};

  return (
    <section className="mt-1">
      <SectionLabel tokens={tokens}>My Overview</SectionLabel>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {overviewCards.map((card) => {
          const target = navigationTargets[card.navigationKey];

          return (
            <OverviewCard
              key={card.label}
              {...card}
              tokens={tokens}
              onClick={target?.path ? () => onNavigate(target.path) : undefined}
            />
          );
        })}
      </div>
    </section>
  );
};

export default ProcurementActivity;
